import {
  Button,
  Divider,
  Form,
  InputNumber,
  Select,
  Space,
  Spin,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IPurchasedProductState } from 'reducers/product-purchase-reducer';
import { InventoryLotWithVariant, getInventoryLotsByVariant } from 'services/inventory';
import { SearchIndex, getSearchData } from 'services/meilisearch';
import { IVariant, getVariant } from 'services/variant';
import styles from './style.module.css';

type AddProductFormProps = {
  purchasedProducts: IPurchasedProductState[];
  onSubmit: (product: IPurchasedProductState) => void;
};

type IOption = {
  id: string;
  name: string;
  sku: string;
};

const { Text } = Typography;
let timeout: ReturnType<typeof setTimeout> | null;
let currentValue: string;

const fetchOptions = (value: string, callback: Function) => {
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }
  currentValue = value;

  const getOptions = async () => {
    const data = await getSearchData(SearchIndex.ProductVariant, value, { limit: 100 });

    if (currentValue === value && value !== '') {
      const options = data
        .map(
          (p) =>
            ({
              id: p.product_variant_id,
              sku: p.sku,
              name: p.name,
            }) as IOption,
        )
        .filter((op) => {
          const lowerCasedValue = value.toLowerCase();
          return (
            op.name.toLowerCase().includes(lowerCasedValue) ||
            op.sku.toLowerCase().includes(lowerCasedValue)
          );
        });
      callback(options);
    }
  };

  timeout = setTimeout(getOptions, 300);
};

export const AddProductForm: React.FC<AddProductFormProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<IVariant | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [lots, setLots] = useState<InventoryLotWithVariant[]>([]);
  const [isOptionLoading, setIsOptionLoading] = useState(false);

  const productSelectRef = useRef<any>(null);
  const quantityRef = useRef<any>(null);

  const [form] = Form.useForm();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  const loadInitialOptions = useCallback(async () => {
    try {
      setIsOptionLoading(true);

      const data = await getSearchData(SearchIndex.ProductVariant, '', { limit: 20 });

      const baseOptions: IOption[] = data.map((p) => ({
        id: p.product_variant_id,
        sku: p.sku,
        name: p.name,
      }));

      setOptions(baseOptions);
    } catch (err: any) {
      notification.error({
        message: 'Failed to load products',
        description: err.message,
      });
    } finally {
      setIsOptionLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialOptions();
  }, [loadInitialOptions]);

  useEffect(() => {
    // Focus on the product select after rendering the form
    if (!form.getFieldValue('variant')) {
      setTimeout(() => {
        productSelectRef.current?.focus();
      }, 0);
    }
  });

  useEffect(() => {
    form.setFields([
      {
        name: 'variant',
        value: selectedOption?.productVariantId,
      },
      { name: 'unitPrice', value: selectedOption?.purchasePrice },
    ]);
  }, [form, selectedOption]);

  const handleProductOptionSearch = (newValue: string) => {
    setIsOptionLoading(true);

    fetchOptions(newValue, async (data: IOption[]) => {
      setOptions(data);
      setIsOptionLoading(false);
    });
  };

  const handleProductOptionChange = (newValue: string) => {
    setSearchText(newValue);
  };

  const renderedProductOptions = options.map((p) => {
    const title = `${p.sku} - ${p.name}`;
    return {
      label: (
        <Typography.Text key={p.id} title={title}>
          {title}
        </Typography.Text>
      ),
      value: p.id,
    };
  });

  const onSelectVariant = async (id: string) => {
    // Focus on the quantity input after selecting the variant
    setTimeout(() => {
      quantityRef.current?.focus();
    }, 0);

    form.resetFields(['quantity']);
    setIsLoading(true);
    try {
      const [variant, lots] = await Promise.all([getVariant(id), getInventoryLotsByVariant(id)]);
      setLots(lots);
      setSelectedOption(variant);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const onFinish = (values: any) => {
    if (!selectedOption) return;

    // Focus on the product select after submitting the form
    setTimeout(() => {
      productSelectRef.current?.focus();
    }, 0);

    const variantId: string = values.variant;
    const unitPrice: number = values.unitPrice;
    const quantity: number = values.quantity;

    const productExists = !!props.purchasedProducts.find(
      (product) => product.productVariantId === variantId,
    );
    if (productExists) {
      form.setFields([
        {
          name: 'variant',
          errors: [`Product is already added.`],
        },
      ]);
      return;
    }

    if (quantity === 0) {
      form.setFields([
        {
          name: 'quantity',
          errors: [`Quantity needs to be greater than 0`],
        },
      ]);
      return;
    }

    props.onSubmit({ ...selectedOption, quantity, unitPrice });
    form.resetFields();
    setLots([]);
    setSelectedOption(null);
  };

  const availableStock = (
    <ul>
      {lots.map((lot) => {
        return lot.quantity > 0 ? (
          <li key={lot.id}>
            <Text>
              {`${lot.quantity} units available | Bought @ BDT ${lot.unitPrice} on
                ${new Date(lot.lotEntryDate).toLocaleDateString('en-GB')}`}
            </Text>
          </li>
        ) : null;
      })}
    </ul>
  );

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = selectedOption?.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const renderedAttributes = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFields = selectedOption?.customFields ?? [];
  const renderedCustomFields = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  const renderNotFound = () => {
    if (isOptionLoading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Spin size="small" />
          <Typography.Text style={{ display: 'block', marginTop: 8, color: '#888' }}>
            Searching products...
          </Typography.Text>
        </div>
      );
    }

    return (
      <div style={{ padding: '12px', textAlign: 'center', color: '#888' }}>No products found</div>
    );
  };

  return (
    <Form layout="vertical" initialValues={{ isActive: true }} form={form} onFinish={onFinish}>
      <Form.Item
        label="Product"
        name="variant"
        rules={[{ required: true, message: 'Please select a product' }]}
      >
        <Select
          ref={productSelectRef}
          showSearch
          placeholder="Select a product"
          value={searchText}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={handleProductOptionSearch}
          onChange={handleProductOptionChange}
          onSelect={onSelectVariant}
          options={renderedProductOptions}
          disabled={isLoading}
          notFoundContent={renderNotFound()}
        />
      </Form.Item>

      {selectedOption && (
        <>
          <Divider />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
              <Typography.Text code>{selectedOption.productName}</Typography.Text>
            </div>

            {renderedAttributes}
            {renderedCustomFields}

            {readPurchasePrice?.can && (
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography.Text strong>DEFAULT PURCHASE PRICE:</Typography.Text>
                <Typography.Text>{selectedOption.purchasePrice}</Typography.Text>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>DEFAULT SELLING PRICE:</Typography.Text>
              <Typography.Text>{selectedOption.sellingPrice}</Typography.Text>
            </div>
          </div>
          <Divider />
        </>
      )}

      <Form.Item
        label="Purchase Price"
        name="unitPrice"
        rules={[{ required: true }, { type: 'number', min: 0 }]}
      >
        <InputNumber disabled={isLoading} style={{ width: '100%' }} min={0} precision={2} />
      </Form.Item>
      <Form.Item
        label="Quantity"
        name="quantity"
        rules={[
          {
            required: true,
            type: 'integer',
            min: 1,
            message: 'Quantity must be a positive integer value',
          },
        ]}
      >
        <InputNumber disabled={isLoading} ref={quantityRef} style={{ width: '100%' }} />
      </Form.Item>

      <Space direction="vertical">
        <Text className={styles['stock__text']}>* Available Stock</Text>
        {availableStock}
      </Space>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
          Add
        </Button>
      </Form.Item>
    </Form>
  );
};
